import { useState } from 'react'
import { eq, useLiveQuery } from '@tanstack/react-db'

import type {
  CalloutLogWorkNotePatch,
  CalloutLogWorkPhotoNoteInput,
  CalloutLogWorkScreenProps,
} from '@/screens/CalloutLogWorkScreen'
import { useDb } from '@/lib/dbContext'
import { getAllowedSessionStatusTransitions } from '@/db/domain/sessionStatus'
import type { User as UserModel } from '@/db/domain/models'
import { getCurrentAssignment } from '@/lib/jobAssignments'
import { buildServiceFeed } from '@/screens/serviceFeed'
import { useEnsureServiceLoaded } from '@/screens/useEnsureServiceLoaded'
import type { Callout, Customer, Note, ServiceAssignment, StatusChangeLog } from '@/lib/schemas/db'
import { useAuth } from '@/lib/auth'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'
import { formatRelativeDateTime } from '@/lib/relativeDateTime'
import { Sentry, withSpan } from '@/lib/sentry'
import { looksLikePublicId } from '@/lib/public-id/routeLookup'
import { captureLegacyIdRoute } from '@/lib/public-id/legacyRouteSentry'

type CalloutLogWorkStatus =
  | 'New'
  | 'Booked'
  | 'Started'
  | 'Ended'
  | 'Review'
  | 'ReadyToInvoice'
  | 'Invoiced'
  | 'Cancelled'
  // Blocked is reachable from anywhere (see withUniversalTransitions) —
  // dropping it here rendered a Blocked session as "New" on Log Work.
  | 'Blocked'

type LogWorkState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'not_found' }
  | {
      kind: 'ready'
      screenProps: CalloutLogWorkScreenProps
      photoNoteError: string | null
      photoNoteRollbackId: string | null
      clearPhotoNoteError: () => void
    }

function mapCalloutStatus(status: Callout['status']): CalloutLogWorkStatus {
  switch (status) {
    case 'New':
    case 'Booked':
    case 'Started':
    case 'Ended':
    case 'Review':
    case 'ReadyToInvoice':
    case 'Invoiced':
    case 'Cancelled':
    case 'Blocked':
      return status
    default:
      return 'New'
  }
}

export function useSessionLogWork(sessionIdentity: string): LogWorkState {
  const db = useDb()
  const { user } = useAuth()
  const [photoNoteError, setPhotoNoteError] = useState<string | null>(null)
  const [photoNoteRollbackId, setPhotoNoteRollbackId] = useState<string | null>(null)
  const tryPublicId = looksLikePublicId(sessionIdentity)

  const sessionByIdResult = useLiveQuery(
    (q) =>
      sessionIdentity
        ? q
            .from({ sessions: db.sessionsCollection })
            .where(({ sessions }) => eq(sessions.id, sessionIdentity))
            .findOne()
        : null,
    [sessionIdentity],
  )

  const sessionByPublicIdResult = useLiveQuery(
    (q) =>
      sessionIdentity && tryPublicId
        ? q
            .from({ sessions: db.sessionsCollection })
            .where(({ sessions }) => eq(sessions.publicId, sessionIdentity))
            .findOne()
        : null,
    [sessionIdentity, tryPublicId],
  )

  const session =
    (sessionByIdResult.data as Callout | undefined) ??
    (sessionByPublicIdResult.data as Callout | undefined)
  const sessionId = session?.id ?? sessionIdentity
  if (!tryPublicId && session && (sessionByIdResult.data as Callout | undefined)) {
    captureLegacyIdRoute('session', sessionIdentity)
  }

  const customerResult = useLiveQuery(
    (q) => {
      if (!session?.customerId) return null
      return q
        .from({ customers: db.customersCollection })
        .where(({ customers }) => eq(customers.id, session.customerId))
        .findOne()
    },
    [session?.customerId],
  )

  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))
  const notesResult = useLiveQuery(
    (q) =>
      sessionId
        ? q
            .from({ notes: db.sessionNotesCollection })
            .where(({ notes }) => eq(notes.serviceId, sessionId))
            .orderBy(({ notes }) => notes.createdAt)
        : null,
    [sessionId],
  )
  const statusChangesResult = useLiveQuery(
    (q) =>
      sessionId
        ? q
            .from({ statusChanges: db.sessionStatusChangesCollection })
            .where(({ statusChanges }) => eq(statusChanges.serviceId, sessionId))
            .orderBy(({ statusChanges }) => statusChanges.createdAt)
        : null,
    [sessionId],
  )
  const assignmentsResult = useLiveQuery(
    (q) =>
      sessionId
        ? q
            .from({ assignments: db.sessionAssignmentsCollection })
            .where(({ assignments }) => eq(assignments.serviceId, sessionId))
        : null,
    [sessionId],
  )

  // Archived (Invoiced/Cancelled) sessions aren't in the live sync; pull one
  // in on demand so the locked Log Work tab still works.
  const recordQueriesSettled =
    !sessionByIdResult.isLoading &&
    !sessionByPublicIdResult.isLoading &&
    !sessionByIdResult.isError &&
    !sessionByPublicIdResult.isError
  const { hydrating } = useEnsureServiceLoaded(
    'session',
    sessionIdentity,
    recordQueriesSettled && !session,
  )

  if (
    hydrating ||
    sessionByIdResult.isLoading ||
    sessionByPublicIdResult.isLoading ||
    customerResult.isLoading ||
    usersResult.isLoading ||
    notesResult.isLoading ||
    statusChangesResult.isLoading ||
    assignmentsResult.isLoading
  ) return { kind: 'loading' }

  if (
    sessionByIdResult.isError ||
    sessionByPublicIdResult.isError ||
    customerResult.isError ||
    usersResult.isError ||
    notesResult.isError ||
    statusChangesResult.isError ||
    assignmentsResult.isError
  ) return { kind: 'error' }

  if (!session) return { kind: 'not_found' }

  const linkedCustomer = customerResult.data as Customer | undefined
  const linkedCustomerName = linkedCustomer
    ? linkedCustomer.externalId
      ? `${linkedCustomer.fullName} – ${linkedCustomer.externalId}`
      : linkedCustomer.fullName
    : session.contactName ?? ''

  const userList = ((usersResult.data ?? []) as UserModel[])
    .map((entry) => ({ id: entry.id, name: entry.name || entry.email }))
    .sort((a, b) => a.name.localeCompare(b.name))
  const usersById = new Map(
    ((usersResult.data ?? []) as UserModel[]).map((entry) => [entry.id, entry]),
  )
  const currentAssignment = getCurrentAssignment(
    (assignmentsResult.data ?? []) as ServiceAssignment[],
    usersById,
  )

  const notes = buildServiceFeed(
    notesResult.data as Note[] | undefined,
    statusChangesResult.data as StatusChangeLog[] | undefined,
    db.getAuthorDisplayName,
  )

  const handleAddTextNote = (text: string) => {
    if (!text.trim()) return
    return Sentry.startSpan(
      { op: 'remote-session.note.add', name: 'Add text note', attributes: { sessionId: session.id } },
      () => {
        const note = db.appendSessionLog(session.id, { text })
        return { id: note.id, createdAt: note.createdAt }
      },
    )
  }

  const handleAddPhotoNote = (input: CalloutLogWorkPhotoNoteInput) => {
    // The caption is optional — a photo with no words is still a note.
    const caption = input.caption.trim()
    setPhotoNoteError(null)
    setPhotoNoteRollbackId(null)

    const { log, transaction } = Sentry.startSpan(
      { op: 'remote-session.note.add_photo', name: 'Add photo note', attributes: { sessionId: session.id } },
      () => db.appendSessionLogWithTransaction(session.id, {
        caption,
        photoUrl: input.photoUrls[0],
        photoUrls: input.photoUrls,
        photoFiles: input.photoFiles,
      }),
    )

    transaction?.isPersisted?.promise
      ?.then(() => setPhotoNoteError(null))
      .catch((error) => {
        setPhotoNoteError(
          formatPocketBaseError(error, {
            fallbackMessage: 'Unable to save photo note. Please try again.',
            fieldLabels: { photo: 'Photo' },
          }),
        )
        setPhotoNoteRollbackId(log.id)
        try {
          if (db.sessionNotesCollection.has?.(log.id)) {
            // Hard delete: the upload failed, so this note never became part
            // of the record and should leave no tombstone.
            db.discardSessionLog(session.id, log.id)
          }
        } catch {}
      })

    return { id: log.id, createdAt: log.createdAt }
  }

  const handleEditNote = (id: string, patch: CalloutLogWorkNotePatch) => {
    db.updateSessionLog(session.id, id, patch)
  }

  const handleDeleteNote = (id: string) => {
    db.deleteSessionLog(session.id, id)
  }

  const handleChangeStatus = async (
    nextStatus: Callout['status'],
    reason?: string,
    occurredAt?: string,
  ) =>
    withSpan(
      { op: 'remote-session.status.change', name: 'Change session status from log work', attributes: { sessionId: session.id, nextStatus } },
      () =>
        db.updateSessionAndWait(session.id, {
          status: nextStatus,
          statusReason: reason?.trim() || undefined,
          statusOccurredAt: occurredAt,
        }),
    )

  const handleAssign = async (userId: string) =>
    withSpan(
      { op: 'remote-session.assign', name: 'Assign session from log work', attributes: { sessionId: session.id, userId } },
      () => db.assignSessionAndWait(session.id, userId),
    )

  const handleCancelCallout = async (reason?: string) => {
    const normalizedReason = reason?.trim()
    if (!normalizedReason) throw new Error('Reason is required when cancelling a session')
    await withSpan(
      { op: 'remote-session.status.change', name: 'Cancel session', attributes: { sessionId: session.id, nextStatus: 'Cancelled' } },
      () =>
        db.updateSessionAndWait(session.id, {
          status: 'Cancelled',
          statusReason: normalizedReason,
        }),
    )
  }

  return {
    kind: 'ready',
    photoNoteError,
    photoNoteRollbackId,
    clearPhotoNoteError: () => {
      setPhotoNoteError(null)
      setPhotoNoteRollbackId(null)
    },
    screenProps: {
      id: session.id,
      publicId: session.publicId,
      resourceLabel: session.sessionSubtype ?? 'TeamViewer Session',
      invoiceRoute: 'session',
      showInvoiceLink: true,
      customerName: linkedCustomerName,
      customerId: session.customerId,
      customerPublicId: linkedCustomer?.publicId,
      issueSummary: session.issueSummary ?? '',
      status: mapCalloutStatus(session.status),
      scheduledAt: session.isWholeDay
        ? `${formatRelativeDateTime(session.startTime, { includeTime: false })} · No Time`
        : formatRelativeDateTime(session.startTime),
      address: session.contactAddress ?? '',
      contactName: session.contactName ?? '',
      contactPhone: session.contactPhone ?? '',
      billables: [],
      notes,
      statusChanges: statusChangesResult.data as StatusChangeLog[] | undefined,
      getAuthorDisplayName: db.getAuthorDisplayName,
      todos: [],
      newNoteAuthorName: user?.name ?? '',
      onAddTextNote: handleAddTextNote,
      onAddPhotoNote: handleAddPhotoNote,
      photoNoteError,
      photoNoteRollbackId,
      onClearPhotoNoteError: () => {
        setPhotoNoteError(null)
        setPhotoNoteRollbackId(null)
      },
      onEditNote: handleEditNote,
      onDeleteNote: handleDeleteNote,
      availableStatusTransitions: getAllowedSessionStatusTransitions(session.status),
      // Forward every argument. A wrapper that forgets a trailing optional
      // param still typechecks, which silently dropped occurredAt once — if you
      // add an argument to handleChangeStatus, add it here too.
      onChangeStatus: (status, reason, occurredAt) =>
        handleChangeStatus(status as Callout['status'], reason, occurredAt),
      users: userList,
      assignedUserId: currentAssignment?.assignment.userId,
      assignedUserName: currentAssignment
        ? currentAssignment.user?.name || currentAssignment.user?.email
        : undefined,
      onAssign: handleAssign,
      onCancelCallout: handleCancelCallout,
    },
  }
}
